import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// Reads the persisted clinics.subscription_status column — the single
// server-trusted record written only by the Stripe webhook (Soft-Close spec §4.1).
// This is the UI's humble reflection of Soft-Close state (CA-11); it does NOT
// perform a live Stripe round-trip (that path is the DDIA-6 SPOF the spec replaces).
export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'soft_closed'
  | 'unpaid'
  | 'incomplete'
  | 'paused'
  | null;

export function useSubscriptionStatus() {
  const { profile } = useAuth();
  const clinicId = profile?.clinic_id ?? null;

  return useQuery<{ status: SubscriptionStatus }>({
    queryKey: ['subscription-status', clinicId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clinics')
        .select('subscription_status')
        .eq('id', clinicId!)
        .single();
      if (error) throw error;
      return { status: (data?.subscription_status ?? null) as SubscriptionStatus };
    },
    enabled: !!clinicId,
    staleTime: 60 * 1000,
    retry: 2,
  });
}
